import React, { useMemo, useState } from 'react';
import { getColorForScore10 } from '../../theme/heatmapPalette';

/**
 * Spreadsheet-style live data view.
 *
 * Rows = tickers. Columns = whatever the user has added. The Aggregate Score
 * column is auto-appended at the right whenever any column has a non-null
 * rubric (i.e. is contributing to a score).
 *
 * Header click → onEditColumn(col). The column header gets a "click to edit"
 * affordance (cursor + faint underline on hover) so it's discoverable.
 *
 * Cells:
 *   - Score columns get the red→green heatmap background and show "raw → score / 10"
 *   - Non-score columns just show the formatted raw value
 *   - Aggregate column gets the heatmap and shows the composite score
 */
function LiveScoringTable({ columns, results, onEditColumn }) {
  const [sortKey, setSortKey] = useState('aggregateScore');
  const [sortDir, setSortDir] = useState('desc');

  const hasScores = useMemo(() => columns.some((c) => isScoringColumn(c)), [columns]);

  const sorted = useMemo(() => {
    return [...results].sort((a, b) => compareResults(a, b, sortKey, sortDir));
  }, [results, sortKey, sortDir]);

  const handleHeaderClickSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'ticker' ? 'asc' : 'desc');
    }
  };

  if (results.length === 0) {
    return (
      <div className="bg-gray-900 border border-gray-700 rounded-lg p-8 text-center text-gray-400 text-sm">
        Add tickers above to see live data here.
      </div>
    );
  }

  return (
    <div className="bg-gray-900 border border-gray-700 rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-gray-800">
              <Th
                label="Ticker"
                isSorted={sortKey === 'ticker'}
                sortDir={sortDir}
                onSort={() => handleHeaderClickSort('ticker')}
                sticky="left-0 z-20"
              />
              {columns.map((col) => (
                <ColumnHeader
                  key={col.id}
                  col={col}
                  isSorted={sortKey === col.id}
                  sortDir={sortDir}
                  onSort={() => handleHeaderClickSort(col.id)}
                  onEdit={() => onEditColumn(col)}
                />
              ))}
              {hasScores && (
                <Th
                  label="★ Score"
                  subtitle="weighted aggregate"
                  isSorted={sortKey === 'aggregateScore'}
                  sortDir={sortDir}
                  onSort={() => handleHeaderClickSort('aggregateScore')}
                />
              )}
            </tr>
          </thead>
          <tbody>
            {sorted.map((row) => (
              <tr key={row.ticker} className="border-t border-gray-800 hover:bg-gray-800/40">
                <td className="px-3 py-1.5 font-mono text-white sticky left-0 z-10 bg-gray-900">
                  {row.ticker}
                </td>
                {columns.map((col) => {
                  const cell = row.cells[col.id];
                  return (
                    <Cell
                      key={col.id}
                      col={col}
                      cell={cell}
                    />
                  );
                })}
                {hasScores && (
                  <td
                    className="px-3 py-1.5 font-semibold text-white whitespace-nowrap"
                    style={getColorForScore10(row.aggregateScore)}
                    title={
                      row.aggregateScore == null
                        ? 'No scoreable data for this ticker'
                        : `Aggregate ${row.aggregateScore.toFixed(2)} · variance ${row.variance.toFixed(2)}`
                    }
                  >
                    {row.aggregateScore == null ? 'N/A' : row.aggregateScore.toFixed(2)}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function ColumnHeader({ col, isSorted, sortDir, onSort, onEdit }) {
  const sub = col.kind === 'derived'
    ? 'derived'
    : col.benchmarkTicker
      ? `vs ${col.benchmarkTicker}`
      : col.fieldKey;
  const scoringTag = scoringTagFor(col);
  return (
    <th
      onClick={onSort}
      className="px-3 py-2 text-left font-medium text-gray-300 cursor-pointer select-none whitespace-nowrap border-b border-gray-700 hover:bg-gray-700 transition group"
    >
      <div className="flex items-center gap-1.5">
        <span className="hover:text-white">{col.displayName || col.fieldKey || 'Untitled'}</span>
        {isSorted && <span className="text-blue-400 text-xs">{sortDir === 'desc' ? '▼' : '▲'}</span>}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onEdit(); }}
          title="Edit column"
          className="ml-auto opacity-50 group-hover:opacity-100 hover:text-blue-400 text-xs transition"
        >⚙</button>
      </div>
      <div className="text-[10px] text-gray-500 normal-case font-normal mt-0.5">
        {sub}
        {scoringTag && <span className="ml-2 text-blue-400">· {scoringTag}</span>}
      </div>
    </th>
  );
}

function Th({ label, subtitle, isSorted, sortDir, onSort, sticky }) {
  const stickyCls = sticky ? `sticky ${sticky} bg-gray-800` : '';
  return (
    <th
      onClick={onSort}
      className={`px-3 py-2 text-left font-medium text-gray-300 cursor-pointer select-none whitespace-nowrap border-b border-gray-700 hover:bg-gray-700 transition ${stickyCls}`}
    >
      <div className="flex items-center gap-1">
        <span>{label}</span>
        {isSorted && <span className="text-blue-400 text-xs">{sortDir === 'desc' ? '▼' : '▲'}</span>}
      </div>
      {subtitle && <div className="text-[10px] text-gray-500 normal-case font-normal">{subtitle}</div>}
    </th>
  );
}

function Cell({ col, cell }) {
  if (!cell) return <td className="px-3 py-1.5 text-gray-500">—</td>;
  const isScoring = isScoringColumn(col);
  const display = isScoring && cell.score != null
    ? `${formatRaw(cell.raw)} → ${cell.score.toFixed(1)}`
    : formatRaw(cell.raw);
  const colorStyle = isScoring && cell.score != null ? getColorForScore10(cell.score) : {};
  return (
    <td
      className={`px-3 py-1.5 whitespace-nowrap ${cell.score == null && isScoring ? 'text-gray-500' : 'text-white'}`}
      style={colorStyle}
      title={cellTooltip(col, cell)}
    >
      {display}
    </td>
  );
}

// ---------------------------------------------------------------------------

function isScoringColumn(col) {
  return !!col.rubric && !!col.rubric.map?.type && (col.weight ?? 0) > 0;
}

function scoringTagFor(col) {
  if (!isScoringColumn(col)) return null;
  const t = col.rubric.map.type;
  const dir = col.rubric.direction || 'higher';
  if (t === 'linear')    return dir === 'lower' ? 'lower better' : 'higher better';
  if (t === 'quantile')  return dir === 'lower' ? 'rank low' : 'rank high';
  if (t === 'threshold') return 'threshold';
  if (t === 'boolean')   return 'yes/no';
  return t;
}

function cellTooltip(col, cell) {
  const lines = [col.displayName || col.fieldKey || 'Column'];
  if (col.benchmarkTicker) lines.push(`Benchmark: ${col.benchmarkTicker}`);
  lines.push(`Raw: ${formatRaw(cell.raw)}`);
  if (cell.transformed != null && cell.transformed !== cell.raw) {
    lines.push(`Transformed: ${formatRaw(cell.transformed)}`);
  }
  if (cell.score != null) lines.push(`Score: ${cell.score.toFixed(2)} / 10`);
  if (cell.bucketHit != null) lines.push(`Bucket #${cell.bucketHit + 1}`);
  if (cell.contribution != null && cell.contribution !== 0) lines.push(`Contribution: ${cell.contribution.toFixed(2)}`);
  if (cell.degraded) lines.push('(degraded — quantile universe too small)');
  return lines.join('\n');
}

function compareResults(a, b, key, dir) {
  const sign = dir === 'desc' ? -1 : 1;
  if (key === 'ticker') return sign * a.ticker.localeCompare(b.ticker);
  let av;
  let bv;
  if (key === 'aggregateScore') {
    av = a.aggregateScore;
    bv = b.aggregateScore;
  } else {
    av = a.cells[key]?.score ?? a.cells[key]?.raw;
    bv = b.cells[key]?.score ?? b.cells[key]?.raw;
  }
  if (av == null && bv == null) return 0;
  if (av == null) return 1;
  if (bv == null) return -1;
  if (typeof av === 'string' && typeof bv === 'string') return sign * av.localeCompare(bv);
  return sign * (bv - av);
}

function formatRaw(v) {
  if (v == null) return 'N/A';
  if (typeof v !== 'number' || !Number.isFinite(v)) return String(v);
  const abs = Math.abs(v);
  if (abs >= 1e9) return `${(v / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `${(v / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return v.toLocaleString(undefined, { maximumFractionDigits: 2 });
  if (abs < 0.01 && abs > 0) return v.toExponential(2);
  return v.toFixed(4).replace(/\.?0+$/, '');
}

export default LiveScoringTable;
